import { Button, IconButton, SelectDropDown } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getEmployeeNameWithEmail } from '../../../../../../../util/table.util'
import { MAILBOX_DELEGATION_ACTIONS } from '../../constants'
import formatTrustee from '../../formatTrustee'
import styles from '../../MailboxDelegations.module.scss'
import { emailsMatch, uniqueEmployeeForTrustee } from '../../trusteeMatch'

const trusteeShape = PropTypes.shape({
  identity: PropTypes.string,
  displayName: PropTypes.string,
  mail: PropTypes.string,
})

const employeeShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  email: PropTypes.string,
  display_name: PropTypes.string,
  first_name: PropTypes.string,
  last_name: PropTypes.string,
})

const propTypes = {
  right: PropTypes.shape({
    key: PropTypes.string.isRequired,
    messageId: PropTypes.string.isRequired,
    defaultMessage: PropTypes.string.isRequired,
    headingMessageId: PropTypes.string,
    headingDefaultMessage: PropTypes.string,
  }).isRequired,
  trustees: PropTypes.arrayOf(trusteeShape),
  candidates: PropTypes.arrayOf(employeeShape),
  pending: PropTypes.bool,
  selected: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
  }),
  onSelect: PropTypes.func,
  onSubmit: PropTypes.func,
}

function DelegationRightEditor({
  right,
  trustees = [],
  candidates = [],
  pending = false,
  selected = null,
  onSelect = () => {},
  onSubmit = () => {},
}) {
  const intl = useIntl()
  const rightLabel = intl.formatMessage({ id: right.messageId, defaultMessage: right.defaultMessage })
  const heading = intl.formatMessage({
    id: right.headingMessageId ?? right.messageId,
    defaultMessage: right.headingDefaultMessage ?? right.defaultMessage,
  })
  const addLabel = intl.formatMessage(
    { id: 'ENGAGE.MAILBOX_DELEGATIONS.ADD_USER', defaultMessage: 'Add a user for {right}' },
    { right: rightLabel },
  )

  const options = useMemo(
    () =>
      candidates
        .filter((employee) => !trustees.some((trustee) => emailsMatch(trustee, employee)))
        .map((employee) => ({
          value: employee.id,
          label: getEmployeeNameWithEmail(employee),
        }))
        .sort((left, rightOption) => left.label.localeCompare(rightOption.label)),
    [candidates, trustees],
  )

  const handleSelect = useCallback(
    (option) => {
      onSelect(right.key, option)
    },
    [onSelect, right.key],
  )

  const handleAdd = useCallback(() => {
    if (!selected) {
      return
    }
    onSubmit(
      {
        right: right.key,
        action: MAILBOX_DELEGATION_ACTIONS.ADD,
        trusteeEmployeeId: selected.value,
      },
      right.key,
    )
  }, [onSubmit, right.key, selected])

  const closeMenuOnScroll = useCallback(
    (event) => !(event.target instanceof Element && event.target.closest('.aug-select__menu')),
    [],
  )

  const handleRemove = useCallback(
    (trustee) => {
      const trusteeIdentity = trustee.identity || trustee.mail
      if (!trusteeIdentity) {
        return
      }
      const matched = uniqueEmployeeForTrustee(trustee, candidates)
      onSubmit(
        {
          right: right.key,
          action: MAILBOX_DELEGATION_ACTIONS.REMOVE,
          trusteeEmployeeId: matched?.id,
          trusteeIdentity,
        },
        right.key,
      )
    },
    [candidates, onSubmit, right.key],
  )

  return (
    <section className={styles.rightBlock} aria-label={rightLabel}>
      <h6 className={clsx('text-bold mb-2', styles.title)}>{heading}</h6>
      {trustees.length === 0 && (
        <p className="text-muted mb-2">
          <FormattedMessage id="GENERAL.NONE" defaultMessage="None" />
        </p>
      )}
      {trustees.map((trustee) => {
        const label = formatTrustee(trustee)
        return (
          <div
            key={trustee.identity}
            className={clsx('d-flex align-items-center justify-content-between mb-2', styles.trusteeRow)}
          >
            <span>{label}</span>
            <IconButton
              icon="fas fa-xmark"
              variant="ghost"
              disabled={pending}
              label={intl.formatMessage(
                {
                  id: 'ENGAGE.MAILBOX_DELEGATIONS.REMOVE_TRUSTEE',
                  defaultMessage: 'Remove {name} from {right}',
                },
                { name: label, right: rightLabel },
              )}
              onClick={() => handleRemove(trustee)}
            />
          </div>
        )
      })}
      <div className={clsx('d-flex align-items-center', styles.addRow)}>
        <div className={styles.addSelect}>
          <SelectDropDown
            id={`mailbox-delegation-add-${right.key}`}
            aria-label={addLabel}
            options={options}
            value={selected}
            defaultLabel={addLabel}
            isDisabled={pending}
            onChange={handleSelect}
            menuPortalTarget={typeof document === 'undefined' ? null : document.body}
            menuPosition="fixed"
            closeMenuOnScroll={closeMenuOnScroll}
          />
        </div>
        <Button variant="primary" disabled={pending || !selected} onClick={handleAdd}>
          <FormattedMessage id="GENERAL.ADD" />
        </Button>
      </div>
    </section>
  )
}

DelegationRightEditor.propTypes = propTypes

export default React.memo(DelegationRightEditor)
